/**
 * Search hook for 統計一覧表 table.
 *
 * Manual-trigger pattern: query is disabled by default. Call refetch()
 * after the user clicks 検索 — mirrors RA2 form-submit behaviour.
 */

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { fetchStatisticalSearch } from '@/services/main/statisticalReport';
import type {
  StatisticalSearchFormValues,
  StatisticalSearchResponse,
} from '@/pages/main/rezepts/statistical/types';

function useServiceType(): string {
  const theme = useTheme();
  return theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
    ? theme.serviceType
    : SERVICE_TYPE_A2;
}

export function useStatisticalSearchQuery() {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const [searchParams, setSearchParams] = useState<StatisticalSearchFormValues | null>(null);

  const enabled = !!tenant && !!clinicNumber && searchParams !== null;

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    StatisticalSearchResponse,
    Error
  >({
    queryKey: ['statistical', 'search', tenant, clinicNumber, serviceType, searchParams],
    queryFn: () =>
      fetchStatisticalSearch(
        tenant,
        clinicNumber,
        searchParams!.searchYearmonth,
        searchParams!.massagerId,
        serviceType,
      ),
    enabled,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  // HIT-14704: every 検索 click re-queries (legacy re-submits the form) so the
  // 「処理中」 modal also appears when the conditions did not change; with an
  // identical queryKey setSearchParams alone would be a no-op.
  const triggerSearch = useCallback(
    (values: StatisticalSearchFormValues) => {
      if (
        searchParams !== null &&
        searchParams.searchYearmonth === values.searchYearmonth &&
        searchParams.massagerId === values.massagerId
      ) {
        void refetch();
        return;
      }
      setSearchParams(values);
    },
    [searchParams, refetch]
  );

  const rows   = data?.data?.rows ?? [];
  const totals = data?.data?.totals ?? null;
  const hasData =
    !!totals?.group1?.tl?.cnt || !!totals?.group2?.tl?.cnt;

  return {
    rows,
    totals,
    hasData,
    hasSearched: searchParams !== null,
    isLoading: isLoading || isFetching,
    triggerSearch,
  };
}
